import { Section } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'
import {
  ArrowOut,
  Copy,
  Delete,
  Edit,
  Favorite,
  FolderClosed,
  LandOnDoc,
  Link,
  Merge,
  Move,
  Share,
  TableView,
  Unfavorite,
  Properties,
  ChevronRight,
  Sort,
  Filter,
  Group,
} from 'almanac-ui/icons'
import { FavoritableType } from 'api/clients/FavoriteClient'
import { Menu } from 'components/Menu/Menu'
import { ShareSheet } from 'components/ShareSheet/ShareSheet'
import { VisualSwitch } from 'components/VisualSwitch/VisualSwitch'
import { Key, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState, UserRole, ViewType } from 'types'
import Styles from './ProjectMenu.module.scss'
import copy from 'copy-to-clipboard'
import { ProjectMenuProps } from './Types'
import { getAlmanacUrlFor, getSearchString, redirectToSignup } from 'utils/urlUtils'
import { FOLDER_ROUTE_PATH } from 'paths'
import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import CopyModal from 'components/CopyModal/CopyModal'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { AbilityContext } from 'components/Can'
import { useAbility } from '@casl/react'
import { useUpdateVcm } from 'hooks/useUpdateVcm'
import { useHandbookMode } from 'components/Handbook2_0/hooks/useHandbookMode'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { useQueryParams } from 'hooks/useQueryParams'
import { useModals } from 'almanac-ui/Modal'
import QueryString from 'qs'
import { useWorkspace } from 'hooks/useWorkspace'
import { useDatabaseMode } from 'components/Handbook2_0/hooks/useDatabaseMode'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useLoggedIn } from 'hooks/useLoggedIn'
import { UserService } from 'api/clients/UserService'
import { useDocumentationFeatures } from 'hooks/useDocumentationFeatures'
import { useOnItemCopied } from 'components/CopyModal/useOnItemCopied'
import { useDeleteProject } from 'hooks/useDeleteProject'
import { useCreateFavorite } from 'hooks/queries/favorites/useCreateFavorite'
import { useDeleteFavorite } from 'hooks/queries/favorites/useDeleteFavorite'
import { useFinderContext } from 'screens/Finder/FinderContext'
import cx from 'classnames'
import { filterGroupsWithoutCalendarProperty } from 'screens/Finder/components/views/CalendarView/utils'
import { Item } from 'components/Menu/MenuItem'
import { ChevronRightSmall } from 'blaze-ui/icons'

const ProjectMenu = observer(({ folder, onAction, hiddenKeys, menuType = 'dropdown', ...props }: ProjectMenuProps) => {
  const workspace = useWorkspace(folder.almanac_file.workspace.id)
  const { openModal } = useModals()
  const { handbooks: handbooksStore } = useRootStore()
  const { deleteProject } = useDeleteProject({
    folderKey: folder.key,
    almanac_file: folder.almanac_file,
  })
  const history = useHistory()
  const location = useLocation()
  const isLoggedIn = useLoggedIn()
  const { updateVcm } = useUpdateVcm(folder.almanac_file.id, 'folder')
  const handbook = handbooksStore.get(folder.handbook.key)
  const { handbookMode, toggleHandbookMode } = useHandbookMode(handbook?.handbook)
  const { databaseMode, toggleDatabaseMode } = useDatabaseMode(handbook?.handbook)
  const openWindowSafely = useOpenWindowSafely()
  const { focusView } = useQueryParams()
  const { isDocumentationEnabled } = useDocumentationFeatures()
  const createFavorite = useCreateFavorite()
  const deleteFavorite = useDeleteFavorite()
  const { filesView } = useFinderContext()
  const { onItemCopied } = useOnItemCopied({
    title: folder.almanac_file.title,
    fileType: 'folder',
    folderSlug: folder.handbook.key,
    handbooksStore,
  })

  useEffect(() => {
    handbooksStore.fetch(folder.handbook.key)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // State
  const { pinned, default_project, almanac_file, has_workflow } = folder
  const favorite = useSelector((state: RootState) =>
    state.favorites.find(
      ({ favoritable_id, favoritable_type }) =>
        favoritable_id === folder.key && favoritable_type === FavoritableType.Project,
    ),
  )
  const isVersionControlOverridden =
    folder.almanac_file?.is_version_controlled && !folder.almanac_file.version_control_mode.value

  const getCanonicalUrl = () => {
    const pathname = generatePath(FOLDER_ROUTE_PATH, { folderSlug: folder.almanac_file.seo_string })
    const search = QueryString.stringify(
      { focusView },
      {
        filter: (_, value) => value ?? undefined,
        addQueryPrefix: true,
      },
    )
    return getAlmanacUrlFor(`${pathname}${search}`)
  }

  // Authorisation
  const ability = useAbility(AbilityContext)
  const authedFolder = {
    // This is a workaround until we redesign how we expose non-accessible workspaces.
    role: workspace?.role || UserRole.UNAUTHED,
    default_project,
    isRoot: almanac_file.is_root,
    permissions: almanac_file.permissions,
    pinned,
    has_workflow,
    __caslSubjectType__: 'Folder',
  }

  const can: typeof ability.can = (action, subject) => {
    if (hiddenKeys?.has(action)) {
      return false
    }
    return ability.can(action, subject)
  }

  // Handlers
  const handleFavorite = () => {
    if (favorite) {
      deleteFavorite.mutate({ id: favorite.id, title: favorite.favoritable_title })
    } else {
      createFavorite.mutate({
        favoritableId: folder.almanac_file.id,
        favoritableType: 'folder',
        favoritableTitle: folder.almanac_file.title,
      })
    }
  }

  const handleShare = () => {
    openModal(ShareSheet, {
      file: {
        id: folder.almanac_file.id,
        type: 'folder',
      },
    })
  }

  const handleCopyLink = () => {
    copy(getCanonicalUrl())
    showToastMessage('Link copied')
  }

  const handleCopy = () => {
    if (!isLoggedIn) {
      const copyPath = encodeURIComponent(`${location.pathname}?${getSearchString({ copyTemplate: 'true' })}`)
      // Clear auth cookie, if not user will receive wrong_scope error and will not be able to log in
      UserService.unsetAuthCookie()
      redirectToSignup(history, { whenDoneRedirectTo: copyPath })
      return
    }

    openModal(CopyModal, {
      itemType: 'folder',
      folder: folder.ancestor_projects[folder.ancestor_projects.length - 1],
      item: {
        id: folder.almanac_file.id,
        title: folder.almanac_file.title,
        header_emoji: folder.almanac_file.emoji,
        linked_copies_count: 0,
      },
      onItemCopied,
    })
  }

  const handleVersionControlMode = () => {
    updateVcm(!folder.almanac_file.version_control_mode.value)
  }

  const filtersCount = filesView ? filterGroupsWithoutCalendarProperty({ filesView }).length : 0

  const handleAction = (key: Key) => {
    if (key === 'openInNewTab') {
      openWindowSafely(
        getAlmanacUrlFor(generatePath(FOLDER_ROUTE_PATH, { folderSlug: folder.almanac_file.seo_string })),
      )
    }

    if (key === 'favorite') {
      handleFavorite()
    }

    if (key === 'share') {
      handleShare()
    }

    if (key === 'copyLink') {
      handleCopyLink()
    }

    if (key === 'copy') {
      handleCopy()
    }

    if (key === 'move') {
      // NOOP: handled by the parent
    }

    if (key === 'rename') {
      // NOOP: handled by the parent
    }

    if (key === 'delete') {
      deleteProject()
    }

    if (key === 'toggleVersionControl') {
      handleVersionControlMode()
    }

    if (key === 'handbookMode') {
      toggleHandbookMode()
    }

    if (key === 'databaseMode') {
      toggleDatabaseMode()
    }

    if (key === 'properties') {
      // NOOP: handled by the parent
    }

    if (key === 'sort') {
      // NOOP: handled by the parent
    }

    if (key === 'filter') {
      // NOOP: handled by the parent
    }

    if (key === 'group') {
      // NOOP: handled by the parent
    }

    if (key === 'openMode') {
      // NOOP: handled by the parent
    }

    onAction?.(key)
  }

  let disabledKeys = new Set(props.disabledKeys)
  if (isVersionControlOverridden) {
    disabledKeys.add('toggleVersionControl')
  }

  const Chevron = menuType === 'tray' ? ChevronRightSmall : undefined

  return (
    <Menu
      menuType={menuType}
      onAction={handleAction}
      disabledKeys={disabledKeys}
      className={cx(Styles.menu, Styles[menuType])}
      aria-label="Folder Menu"
      itemClassName={Styles.menuItem}
    >
      <Section>
        <Item key="openInNewTab" textValue="Open in new tab" frontIcon={ArrowOut}>
          Open in new tab
        </Item>
      </Section>
      <Section>
        {
          [
            can('favorite', authedFolder) && (
              <Item key="favorite" textValue="Add to Favorites" frontIcon={favorite ? Unfavorite : Favorite}>
                {favorite ? 'Remove from Favorites' : 'Add to Favorites'}
              </Item>
            ),
            can('share', authedFolder) && (
              <Item key="share" textValue="Share" frontIcon={Share} endIcon={Chevron}>
                Share
              </Item>
            ),
            can('copyLink', authedFolder) && (
              <Item key="copyLink" textValue="Copy Link" frontIcon={Link}>
                Copy Link
              </Item>
            ),
            can('copy', authedFolder) && (
              <Item key="copy" textValue="Make a Copy" frontIcon={Copy} endIcon={Chevron}>
                Make a Copy
              </Item>
            ),
            can('move', authedFolder) && (
              <Item key="move" textValue="Move" frontIcon={Move} endIcon={Chevron}>
                Move
              </Item>
            ),
            can('rename', authedFolder) && (
              <Item key="rename" textValue="Rename" frontIcon={Edit}>
                Rename
              </Item>
            ),
          ].filter(Boolean) as ItemElement<HTMLElement>[]
        }
      </Section>
      {can('updateMetadata', authedFolder) && filesView?.schema.default_view === ViewType.Table && (
        <Section>
          <Item key="properties" textValue="Properties" frontIcon={Properties}>
            <span className={Styles.label}>Properties</span>
            <span className={Styles.description}>{filesView.schema.properties.length} shown</span>
            <ChevronRight />
          </Item>

          <Item key="sort" textValue="Sort" frontIcon={Sort}>
            <span className={Styles.label}>Sort</span>
            <span className={Styles.description}>
              {filesView.schema.sorting_property ? `By ${filesView.schema.sorting_property.name}` : 'None'}
            </span>
            <ChevronRight />
          </Item>

          <Item key="filter" textValue="Filter" frontIcon={Filter}>
            <span className={Styles.label}>Filter</span>
            <span className={Styles.description}>{filtersCount || 'None'}</span>
            <ChevronRight />
          </Item>

          <Item key="group" textValue="Group" frontIcon={Group}>
            <span className={Styles.label}>Group</span>
            <span className={Styles.description}>
              {filesView.schema.grouping_property ? `By ${filesView.schema.grouping_property.name}` : 'None'}
            </span>
            <ChevronRight />
          </Item>
        </Section>
      )}
      <Section>
        {
          [
            isDocumentationEnabled && can('toggleVersionControl', authedFolder) && (
              <Item key="toggleVersionControl" textValue="Version Control Mode" frontIcon={Merge}>
                <Tooltip
                  placement="left"
                  label={
                    folder.almanac_file.workspace?.version_control_mode.value
                      ? 'Version Control Mode is ON for the workspace'
                      : 'Version Control Mode is ON for the folder this file is in'
                  }
                  hide={!isVersionControlOverridden}
                >
                  <div className={Styles.tooltipOverlay} />
                </Tooltip>
                <span className={Styles.label}>Version Control Mode</span>
                <VisualSwitch active={folder.almanac_file.is_version_controlled} variant="sm" />
              </Item>
            ),
            isDocumentationEnabled && can('updateMetadata', authedFolder) && !folder.database_mode && (
              <Item key="handbookMode" textValue="Default to First Doc" frontIcon={LandOnDoc}>
                <span className={Styles.label}>Default to First Doc</span>
                <VisualSwitch active={handbookMode} variant="sm" />
              </Item>
            ),
            isDocumentationEnabled && can('updateMetadata', authedFolder) && (
              <Item
                key="databaseMode"
                textValue={databaseMode ? 'Convert to Folder' : 'Convert to Database'}
                frontIcon={databaseMode ? FolderClosed : TableView}
              >
                <span className={Styles.label}>{databaseMode ? 'Convert to Folder' : 'Convert to Database'}</span>
              </Item>
            ),
            can('delete', authedFolder) && (
              <Item key="delete" textValue="Delete" frontIcon={Delete}>
                Delete Project
              </Item>
            ),
          ].filter(Boolean) as ItemElement<HTMLElement>[]
        }
      </Section>
    </Menu>
  )
})

export { ProjectMenu }
